import * as React from 'react';
import {useRef, useCallback} from 'react';
import {
  createView,
  view,
  dynamic,
  animate,
  AnimatePresence,
  assignProps,
} from '@sail/engine';
import {FocusScope, useControlledState} from '@sail/react-aria';
import {bottomSheetStyles, bottomSheetAnimation} from '../../utilStyles/sheet';
import {useNestedSheets} from '../../hooks/useNestedSheets';
import {useProps} from '../../hooks/useProps';
import {useOverlayIframe} from '../../iframe-overrides/OverlayOverrides';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {Layer} from '../layers/Layer';
import {ButtonConfig} from '../Button';
import {SelectConfig} from '../Select';
import {TextFieldConfig} from '../TextField';
import {SelectFieldConfig} from '../SelectFieldConfig';
import {BottomSheetOverlay} from '../BottomSheetOverlay';
import {PopoverHeader} from './PopoverHeader';
import {replaceDeprecatedProps} from './utils';
import {Backdrop} from '../Backdrop';
import type {PopoverProps} from './PopoverProps';

export const MobilePopover = createView<PopoverProps>((allProps) => {
  const {
    targetRef,
    placement: reactAriaPlacement,
    offset: offsetOptions,
    flip: flipEnabled,
    shift: shouldShift,
    size: sizeOptions,
    inline: inlineEnabled,
    hide: hideOptions,
    onClose,
    subviews,
    dismissible,
    showOnPress,
    showOnFocus,
    showOnHover,
    role,
    defaultOpen,
    open: controlledOpen,
    mobileTitle,
    title,
    onOpenChange: setControlledOpen,
    floatingStrategy,
    animate: shouldAnimate = true,
    useReactAriaDismissOutside,
    children,
    // We don't want to spread inherits to the Popover mobile, as people
    // will be thinking about the desktop version of the Popover when they
    // pass them. If we ever want to allow bottom sheet customization,
    // we can then expose a subview.
    inherits,
    ...props
  } = replaceDeprecatedProps(allProps);

  const ref = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useControlledState(
    controlledOpen ?? (defaultOpen === undefined ? true : undefined),
    defaultOpen,
    setControlledOpen,
  );

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        onClose?.();
      }
    },
    [setOpen, onClose],
  );

  const uiConfig = useUiConfig();

  const isUncontrolled = typeof controlledOpen === 'undefined';

  useProps(targetRef, {
    onClick: () => {
      if (isUncontrolled) {
        onOpenChange(!open);
      }
    },
  });

  const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;

  const layer = (
    <Layer
      inherits={subviews.layer}
      name="modal"
      uses={
        shouldApplyIframeOverrides ? [assignProps(useOverlayIframe)] : undefined
      }
      iosScrollLock
    >
      <Backdrop
        onClose={() => onOpenChange(false)}
        uses={[
          animate.inout({
            duration: 150,
            opacity: 0,
            easing: 'ease-in-out',
          }),
        ]}
      />
      <FocusScope restoreFocus>
        <view.div
          // declare this before spreading props so user's test id wins out, but we can
          // still use this test id for sail next tests
          data-testid="popover-layer"
          {...props}
          inherits={subviews.mobile}
          ref={ref}
          css={{stack: 'y'}}
          uses={[
            dynamic(() => {
              const {sheetStackProvider} = useNestedSheets(ref, 'bottom-sheet');
              return [sheetStackProvider];
            }),
            bottomSheetAnimation(!shouldAnimate),
            ButtonConfig.customize({defaults: {size: 'large'}}),
            SelectConfig.customize({defaults: {size: 'large'}}),
            SelectFieldConfig.customize({defaults: {size: 'large'}}),
            TextFieldConfig.customize({defaults: {size: 'large'}}),

            bottomSheetStyles,
          ]}
        >
          <BottomSheetOverlay />
          <PopoverHeader
            onClose={() => onOpenChange?.(false)}
            title={mobileTitle || title}
          />
          {children}
        </view.div>
      </FocusScope>
    </Layer>
  );

  return <AnimatePresence>{open ? layer : null}</AnimatePresence>;
});
